import type { HouseBodyConfig, LotConfig } from '@/model/schema';
import type { ResolvedConstraints } from './constraints';
import type { StylePresetSpec } from './stylePresets';
import type { Rng } from './rng';

/**
 * Place a rectangular footprint inside the lot's buildable (setback-inset)
 * region. Width/depth derived from the preset's footprint coverage and aspect
 * ratio distributions.
 *
 * Coordinate convention: origin is the lot center at y=0. +X is right, +Z is
 * into the scene (so "front" is at the -Z edge).
 */
export function generateFootprint(
  rng: Rng,
  lot: LotConfig,
  preset: StylePresetSpec,
  constraints: ResolvedConstraints,
): HouseBodyConfig {
  const buildableW = Math.max(1, lot.width - lot.setbackLeft - lot.setbackRight);
  const buildableD = Math.max(1, lot.depth - lot.setbackFront - lot.setbackBack);
  const buildableArea = buildableW * buildableD;

  const coverage = rng.range(preset.footprintCoverage.min, preset.footprintCoverage.max);
  const targetArea = buildableArea * coverage;

  // Aspect ratio = footprintWidth / footprintDepth
  let aspect = rng.range(preset.aspectRatio.min, preset.aspectRatio.max);

  // Derive width/depth from target area + aspect: area = w*d, w = aspect*d.
  let footprintDepth = Math.sqrt(targetArea / aspect);
  let footprintWidth = aspect * footprintDepth;

  // Clamp into buildable region; re-derive aspect if clamping fires.
  if (footprintWidth > buildableW) {
    footprintWidth = buildableW;
    footprintDepth = targetArea / footprintWidth;
  }
  if (footprintDepth > buildableD) {
    footprintDepth = buildableD;
    footprintWidth = Math.min(buildableW, targetArea / footprintDepth);
  }
  footprintWidth = Math.max(5.5, footprintWidth);
  footprintDepth = Math.max(5.5, footprintDepth);
  footprintWidth = Math.min(footprintWidth, buildableW);
  footprintDepth = Math.min(footprintDepth, buildableD);
  aspect = footprintWidth / footprintDepth;

  // Position inside buildable region. Keep the front setback tight (nicer
  // street presence), and center laterally with a small jitter for variety.
  const lateralSlack = Math.max(0, buildableW - footprintWidth);
  const depthSlack = Math.max(0, buildableD - footprintDepth);

  const originX = -lot.width / 2 + lot.setbackLeft + lateralSlack * rng.range(0.35, 0.65);
  // Prefer toward the back so front yard exists.
  const originZ = -lot.depth / 2 + lot.setbackFront + depthSlack * rng.range(0.6, 0.9);

  const storyHeight = rng.range(preset.storyHeight.min, preset.storyHeight.max);

  return {
    footprintWidth,
    footprintDepth,
    originX,
    originZ,
    storyHeight,
    exteriorWallThickness: 0.2,
    floorThickness: 0.18,
    stylePreset: constraints.stylePreset.value,
    // Plinth defaults to none; `generateHouse` may upgrade it via decor RNG.
    plinthHeight: 0,
    hasFrontSteps: false,
  };
}
